import axios from "axios";

const API_URL = "/api/trainers";

// =====================================================
// COMMON HEADERS
// =====================================================

const getHeaders = () => {
    const token = localStorage.getItem("token");

    return {
        headers: {
            "Content-Type": "application/json",
            ...(token
                ? {
                      Authorization: `Bearer ${token}`
                  }
                : {})
        }
    };
};


// =====================================================
// PROFILE
// =====================================================

export const getTrainerProfile = async (trainerId) => {

    const response = await axios.get(
        `${API_URL}/${trainerId}/profile`,
        getHeaders()
    );

    return response.data;
};


export const saveTrainerDetails = async (
    trainerId,
    data
) => {

    const response = await axios.post(
        `${API_URL}/${trainerId}/details`,
        data,
        getHeaders()
    );

    return response.data;
};


export const updateTrainerDetails = async (
    trainerId,
    data
) => {

    const response = await axios.put(
        `${API_URL}/${trainerId}/details`,
        data,
        getHeaders()
    );

    return response.data;
};


// =====================================================
// PROFILE PICTURE
// =====================================================

/**
 * Upload trainer profile picture
 *
 * Backend:
 * POST /api/trainers/{trainerId}/details/profile-picture
 *
 * Field name:
 * profilePicture
 */
export const uploadProfilePicture = async (
    trainerId,
    file
) => {

    if (!trainerId) {
        throw new Error("Trainer ID is required.");
    }

    if (!file) {
        throw new Error(
            "Please select a profile picture."
        );
    }

    // Allowed image types
    const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/webp"
    ];

    if (!allowedTypes.includes(file.type)) {
        throw new Error(
            "Only JPG, PNG and WEBP images are allowed."
        );
    }

    // Maximum 5 MB
    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
        throw new Error(
            "Profile picture must be smaller than 5 MB."
        );
    }

    const formData = new FormData();

    formData.append(
        "profilePicture",
        file
    );

    const token =
        localStorage.getItem("token");

    const response = await axios.post(
        `${API_URL}/${trainerId}/details/profile-picture`,
        formData,
        {
            headers: {
                ...(token
                    ? {
                          Authorization:
                              `Bearer ${token}`
                      }
                    : {})

                // IMPORTANT:
                // Do NOT manually set Content-Type.
                // Axios will automatically set:
                // multipart/form-data + boundary
            }
        }
    );

    return response.data;
};


// =====================================================
// GET PROFILE PICTURE URL
// =====================================================

export const getProfilePictureUrl = (
    trainerId
) => {

    if (!trainerId) {
        return null;
    }

    return `${API_URL}/${trainerId}/details/profile-picture`;
};


// =====================================================
// DELETE PROFILE PICTURE
// =====================================================

export const deleteProfilePicture = async (
    trainerId
) => {

    if (!trainerId) {
        throw new Error(
            "Trainer ID is required."
        );
    }

    const token =
        localStorage.getItem("token");

    const response = await axios.delete(
        `${API_URL}/${trainerId}/details/profile-picture`,
        {
            headers: {
                ...(token
                    ? {
                          Authorization:
                              `Bearer ${token}`
                      }
                    : {})
            }
        }
    );

    return response.data;
};


// =====================================================
// EDUCATION
// =====================================================

export const addEducation = async (
    trainerId,
    data
) => {

    const response = await axios.post(
        `${API_URL}/${trainerId}/education`,
        data,
        getHeaders()
    );

    return response.data;
};


export const updateEducation = async (
    educationId,
    data
) => {

    const response = await axios.put(
        `${API_URL}/education/${educationId}`,
        data,
        getHeaders()
    );

    return response.data;
};


export const deleteEducation = async (
    educationId
) => {

    const response = await axios.delete(
        `${API_URL}/education/${educationId}`,
        getHeaders()
    );

    return response.data;
};


// =====================================================
// EXPERIENCE
// =====================================================

export const addExperience = async (
    trainerId,
    data
) => {

    const response = await axios.post(
        `${API_URL}/${trainerId}/experience`,
        data,
        getHeaders()
    );

    return response.data;
};


export const updateExperience = async (
    experienceId,
    data
) => {

    const response = await axios.put(
        `${API_URL}/experience/${experienceId}`,
        data,
        getHeaders()
    );

    return response.data;
};


export const deleteExperience = async (
    experienceId
) => {

    const response = await axios.delete(
        `${API_URL}/experience/${experienceId}`,
        getHeaders()
    );

    return response.data;
};